import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import RequestV2 from "../../../api/RequestV2";
import Config from "../../../utils/config";
import { toast } from "react-toastify";
import {
    Card,
    PageBodyTitle,
    PageLoader,
    Table,
    StatusBadge,
    Button,
} from "../../design-system";
import { theme, Badge } from "../../design-system";
import styled from "styled-components";
import { Menu, MenuItem } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: ${theme.spacing.lg};
`;
const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
  width: fit-content;
`;
const Label = styled.span`
  font-size: 12px;
  color: ${theme.colors.textSecondary};
  font-weight: 500;
`;
const Value = styled.span`
  font-size: 16px;
  color: ${theme.colors.textPrimary};
  font-weight: 600;
`;
const MandateStatusBadge = styled(Badge)`
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  ${({ colorType }) => {
        switch (colorType) {
            case "paid": return `background:#DCFCE7; color:#16A34A;`;
            case "partial": return `background:#FEF3C7; color:#F59E0B;`;
            case "active": return `background:#DBEAFE; color:#2563EB;`;
            case "upcoming": return `background:#F3F4F6; color:#9CA3AF;`;
            case "failed": return `background:#FEE2E2; color:#DC2626;`;
            default: return `background:#F3F4F6; color:#6B7280;`;
        }
    }}
`;
const PageHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.25rem;
`;

export default function ViewMandateDetail() {
    const { mandateNo } = useParams();
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState({});
    const [schedule, setSchedule] = useState([]);
    const location = useLocation();
    const {
        userName,
        mobileNumber,
        fromRevokeMandate = false,
        requestId,
        revokeStatus,
    } = location.state || {};
    const [currentRevokeStatus, setCurrentRevokeStatus] = useState(revokeStatus);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [remarks, setRemarks] = useState("");
    const [actionModal, setActionModal] = useState({
        open: false,
        status: "",
    });
    const openActionModal = (status) => {
        setActionModal({
            open: true,
            status,
        });
    };
    const closeModal = () => {
        setActionModal({
            open: false,
            status: "",
        });
        setRemarks("");
    };

    useEffect(() => {
        fetchMandateDetails();
    }, [mandateNo]);

    const paginatedSchedule = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return schedule.slice(start, start + pageSize);
    }, [schedule, currentPage, pageSize]);

    const isCurrentActiveInstallment = (row) => {
        if ((row.fundStatusName || row.fundStatus)?.toUpperCase() !== "UPCOMING") {
            return false;
        }
        const currentIndex = schedule.findIndex(
            (item) => item.installmentNo === row.installmentNo
        );
        if (currentIndex <= 0) return false;
        const previous = schedule[currentIndex - 1];
        const previousStatus = (
            previous.fundStatusName || previous.fundStatus || ""
        ).toUpperCase();
        return previousStatus === "PAID" || previousStatus === "FULLY PAID";
    };

    const submitAction = () => {
        const trimmedRemarks = remarks.trim();
        const api = new RequestV2(
            "",
            (res) => {
                toast.success(res?.data || res?.message || "Success");
                if (actionModal.status !== "CANCELLED") {
                    setCurrentRevokeStatus(actionModal.status);
                }
                closeModal();
                fetchMandateDetails();
            },
            (err) => {
                toast.error(
                    err?.response?.data?.message ||
                    err?.message ||
                    "Something went wrong"
                );
            }
        );

        if (actionModal.status === "CANCELLED") {
            api.post(Config.apis.user.UPDATE_MANDATE_STATUS, {
                mandateNo,
                status: "CANCELLED",
                reason: trimmedRemarks,
            });
        } else {
            api.post(Config.apis.user.ACTION_REVOKE_MANDATE, {
                requestId,
                status: actionModal.status,
                remarks: trimmedRemarks,
            });
        }
    };

    const fetchMandateDetails = () => {
        setLoading(true);

        const api = new RequestV2(
            "",
            (res) => {
                setDetail(res);
                setSchedule(res.paymentSchedule || []);
                setLoading(false);
            },
            (err) => {
                setLoading(false);
                const response = err?.response?.data || err?.data || err;
                if (response?.fieldErrors?.length) {
                    response.fieldErrors.forEach((e) => toast.error(e.message));
                    return;
                }
                toast.error(response?.message || "Something went wrong");
            }
        );
        api.get(`${Config.apis.user.MANDATE_DUES}/${mandateNo}`);
    };

    function RevokeActionButton() {
        const [anchor, setAnchor] = useState(null);
        const close = () => setAnchor(null);
        return (
            <>
                <Button
                    type="button"
                    variant="outline"
                    endIcon={<ExpandMoreIcon />}
                    onClick={(e) => setAnchor(e.currentTarget)}
                    style={{ padding: "8px", minHeight: "20px" }}
                >
                    Action
                </Button>
                <Menu
                    anchorEl={anchor}
                    open={Boolean(anchor)}
                    onClose={close}
                >
                    <MenuItem
                        sx={{
                            color: "#16A34A",
                            fontWeight: 500,
                            fontSize: 12,
                            "&:hover": {
                                backgroundColor: "#DCFCE7",
                            },
                        }}
                        onClick={() => {
                            close();
                            openActionModal("APPROVED");
                        }}
                    >
                        Approve
                    </MenuItem>
                    <MenuItem
                        sx={{
                            color: "#DC2626",
                            fontWeight: 500,
                            fontSize: 12,
                            "&:hover": {
                                backgroundColor: "#FEE2E2",
                            },
                        }}
                        onClick={() => {
                            close();
                            openActionModal("REJECTED");
                        }}
                    >
                        Reject
                    </MenuItem>
                </Menu>
            </>
        );
    }

    const columns = [
        {
            key: "installmentNo",
            label: "Installment",
            render: (_, row) => row.installmentNo,
        },
        {
            key: "dueDate",
            label: "Due Date",
            render: (_, row) => row.dueDate,
        },
        {
            key: "amount",
            label: "Amount",
            align: "right",
            render: (_, row) => row.amount,
        },
        {
            key: "deductedAmount",
            label: "Deducted Amount",
            align: "right",
            render: (_, row) => row.deductedAmount,
        },
        {
            key: "fundStatus",
            label: "Fund Status",
            render: (_, row) => {
                const status = row.fundStatusName || row.fundStatus;
                let colorType = "upcoming";
                switch (status?.toUpperCase()) {
                    case "PAID":
                    case "FULLY PAID": colorType = "paid";
                        break;
                    case "PARTIALLY PAID":
                    case "PARTIALLY_PAID": colorType = "partial";
                        break;
                    case "FAILED": colorType = "failed";
                        break;
                    case "UNPAID": colorType = "failed";
                        break;
                    case "CANCELLED": colorType = "failed";
                        break;
                    case "UPCOMING": colorType = isCurrentActiveInstallment(row) ? "active" : "upcoming";
                        break;
                    default:
                        break;
                }
                return (
                    <MandateStatusBadge colorType={colorType}>
                        {status}
                    </MandateStatusBadge>
                );
            },
        },
        {
            key: "settlementStatus",
            label: "Settlement Status",
            render: (_, row) => (
                <MandateStatusBadge status={row.settlementStatus}>
                    {row.settlementStatus}
                </MandateStatusBadge>
            ),
        },
        {
            key: "transactionId",
            label: "Transaction Id",
            render: (_, row) => row.transactionId || "—",
        },
        {
            key: "settlementRefNo",
            label: "Settlement Ref No",
            render: (_, row) => row.settlementRefNo || "—",
        },
    ];

    if (loading) {
        return <PageLoader />;
    }

    return (
        <>
            <PageHeader>
                <PageBodyTitle style={{ marginBottom: theme.spacing.md }}>
                    AutoPay Details
                </PageBodyTitle>
                {fromRevokeMandate && currentRevokeStatus === "PENDING" && (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            marginBottom: theme.spacing.md,
                        }}
                    >
                        <RevokeActionButton />
                    </div>
                )}
                {detail.status === "ACTIVE" && !fromRevokeMandate && (
                    <Button
                        variant="outline"
                        color="error"
                        onClick={() => openActionModal("CANCELLED")}
                        style={{ minHeight: "25px", color: "#d32f2f", borderColor: "#d32f2f", }}
                    >
                        Revoke
                    </Button>
                )}
            </PageHeader>
            <Card style={{ marginBottom: theme.spacing.lg }}>
                <DetailGrid>
                    <DetailItem>
                        <Label>User Name</Label>
                        <Value>{userName || "—"}</Value>
                    </DetailItem>
                    <DetailItem>
                        <Label>Mobile Number</Label>
                        <Value>{mobileNumber || "—"}</Value>
                    </DetailItem>
                    <DetailItem>
                        <Label>Status</Label>
                        <StatusBadge status={detail.status} />
                    </DetailItem>
                    <DetailItem>
                        <Label>Frequency</Label>
                        <Value>{detail.frequency || "—"}</Value>
                    </DetailItem>
                    <DetailItem>
                        <Label>Total Amount</Label>
                        <Value>{detail.totalAmount || "—"}</Value>
                    </DetailItem>
                </DetailGrid>
            </Card>
            <Card>
                <Table
                    data={paginatedSchedule}
                    columns={columns}
                    rowKey={(row) => row.installmentNo}
                    loading={loading}
                    emptyMessage="No payment schedule found"
                    pagination={{
                        currentPage,
                        pageSize,
                        total: schedule.length,
                        onPageChange: setCurrentPage,
                        pageSizeOptions: [10, 20, 50],
                        onPageSizeChange: (size) => {
                            setPageSize(size);
                            setCurrentPage(1);
                        },
                    }}
                />
            </Card>
            <Dialog
                open={actionModal.open}
                onClose={closeModal}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    {actionModal.status === "APPROVED"
                        ? "Approve Revoke Request"
                        : actionModal.status === "REJECTED"
                            ? "Reject Revoke Request"
                            : "Revoke AutoPay"}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        {actionModal.status === "APPROVED"
                            ? "Are you sure you want to approve this revoke request?"
                            : actionModal.status === "REJECTED"
                                ? "Are you sure you want to reject this revoke request?"
                                : "Are you sure you want to revoke this autopay?"}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Remarks*"
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        inputProps={{ maxLength: 150 }}
                        helperText={`${remarks.length}/150`}
                    />
                </DialogContent>
                <DialogActions>
                    <Button variant="outline" onClick={closeModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!remarks.trim()}
                        onClick={submitAction}
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
